import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLookups } from "@/hooks/useLookups";
import { api, errMsg } from "@/lib/api";

const KEEP = "__keep";

function Result({ r }) {
  const line = (k, l, cls) => r[k].length > 0 && (
    <div className={cls} data-testid={`ag-bulk-result-${k}`}><b>{l} ({r[k].length}):</b> {r[k].map((x) => `${x.code}${x.reason ? ` - ${x.reason}` : ""}`).join("; ")}</div>);
  return <div className="space-y-1 text-xs">{line("updated", "Updated", "text-emerald-800")}{line("partial", "Partly updated", "text-amber-800")}{line("unchanged", "Already had these values", "text-slate-600")}{line("failed", "Not updated", "text-red-700")}</div>;
}

// Bulk set contract type and / or IoT status on the selected Asset Groups (confirm step, then a per-group result)
export function GroupBulkUpdate({ ids, onClose, onDone }) {
  const { opts } = useLookups();
  const [ct, setCt] = useState(KEEP);
  const [iot, setIot] = useState(KEEP);
  const [step, setStep] = useState("edit");
  const [res, setRes] = useState(null);
  const changes = [ct !== KEEP && `contract type → ${ct}`, iot !== KEEP && `IoT installed → ${iot === "yes" ? "Yes" : "No"}`].filter(Boolean);
  const apply = async () => {
    setStep("busy");
    try {
      const r = await api.post("/assets/groups/bulk-update", { ids, ...(ct !== KEEP ? { contract_type: ct } : {}), ...(iot !== KEEP ? { iot_installed: iot === "yes" } : {}) });
      setRes(r.data); setStep("done"); onDone();
      toast.success(`${r.data.updated.length + r.data.partial.length} updated, ${r.data.failed.length} not updated`);
    } catch (e) { toast.error(errMsg(e)); setStep("edit"); }
  };
  const pick = (l, v, set, options, tid) => (
    <div><Label>{l}</Label><Select value={v} onValueChange={set}><SelectTrigger data-testid={tid} disabled={step !== "edit"}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={KEEP}>Keep as is</SelectItem>{options.map(([k, t]) => <SelectItem key={k} value={k}>{t}</SelectItem>)}</SelectContent></Select></div>);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ag-bulk-dialog">
        <DialogHeader><DialogTitle>Bulk update {ids.length} asset group(s)</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {pick("Contract type", ct, setCt, opts("contract_type").map((x) => [x, x]), "ag-bulk-contract-type")}
          {pick("IoT status", iot, setIot, [["yes", "IoT installed"], ["no", "No IoT"]], "ag-bulk-iot")}
        </div>
        <p className="text-[11px] text-slate-500">Groups linked to an LMO take their contract type from the LMO and are skipped for contract type. AMC / CAMC needs contract dates on the group. Every change is recorded in each group's history.</p>
        {step === "confirm" && <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="ag-bulk-confirm">Apply {changes.join(" and ")} to {ids.length} group(s)?</div>}
        {res && <Result r={res} />}
        <DialogFooter className="gap-2">
          {step === "edit" && <Button disabled={!changes.length} onClick={() => setStep("confirm")} data-testid="ag-bulk-review">Review</Button>}
          {step === "confirm" && <><Button variant="ghost" onClick={() => setStep("edit")} data-testid="ag-bulk-back">Back</Button><Button onClick={apply} data-testid="ag-bulk-apply">Yes, apply</Button></>}
          {step === "busy" && <Button disabled>Applying…</Button>}
          {step === "done" && <Button onClick={onClose} data-testid="ag-bulk-close">Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
